import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useState, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { noop } from "lodash/fp";
import { UtilityButton } from "../utility-button/utility-button.es.js";
import { InPlaceInputWrapper, InPlaceInputTextArea, InPlaceInputButtonWrapper } from "./in-place-input.styles.es.js";
import "@reach/auto-id";
import { useKeyboardShortcut } from "../../hooks/use-keyboard-shortcut.es.js";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import { useEditState } from "../../hooks/use-edit-state.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import "@reach/rect";
import "compute-scroll-into-view";
import { deprecated } from "../../prop-types-validators/deprecated.es.js";
import { isControlCharacter } from "../../utils/is-control-character.es.js";
const propTypes = {
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  hideEditButtonOnDisabled: PropTypes.bool,
  keyboardShortcut: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: deprecated(PropTypes.func, "Use onChange prop instead."),
  placeholder: PropTypes.string,
  preventScroll: PropTypes.bool,
  value: PropTypes.string,
  variant: PropTypes.oneOf(["description", "title"])
};
const defaultProps = {
  autoFocus: false,
  disabled: false,
  hideEditButtonOnDisabled: false,
  keyboardShortcut: void 0,
  placeholder: "",
  preventScroll: false,
  onChange: void 0,
  onSubmit: void 0,
  value: "",
  variant: "description"
};
const InPlaceInput = forwardRef(
  ({
    autoFocus,
    disabled,
    hideEditButtonOnDisabled,
    keyboardShortcut,
    onChange,
    onSubmit,
    placeholder,
    preventScroll,
    value,
    variant
  }, forwardedRef) => {
    const textAreaRef = useRef();
    const [editMode, setEditMode] = useState(autoFocus);
    const [inputValue, setInputValue] = useEditState(
      [value, onChange || onSubmit],
      editMode
    );
    const [textAreaHeight, setTextAreaHeight] = useState(24);
    const keyboardRef = useKeyboardShortcut({
      predicate: keyboardShortcut,
      callback: () => setEditMode(true)
    });
    const enterEditMode = () => {
      setEditMode(true);
    };
    const commitChanges = () => {
      setEditMode(false);
    };
    const resetChanges = () => {
      setInputValue(value);
      setEditMode(false);
    };
    const handleChange = (event) => {
      setInputValue(event.currentTarget.value);
    };
    const handleKeyDown = (event) => {
      if (!event.ctrlKey && !event.metaKey && !isControlCharacter(event.key)) {
        event.stopPropagation();
      }
      switch (event.key) {
        case "Escape":
          resetChanges();
          break;
        case "Enter":
          if (variant === "title" || !event.shiftKey) {
            event.preventDefault();
            commitChanges();
          }
          break;
      }
    };
    useEffect(
      () => () => commitChanges(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );
    useEffect(() => {
      if (editMode && textAreaRef.current) {
        textAreaRef.current.focus({ preventScroll });
        textAreaRef.current.select();
      }
    }, [editMode, preventScroll]);
    useEffect(() => {
      if (disabled) {
        setEditMode(false);
      }
    }, [disabled]);
    useEffect(() => {
      const { scrollHeight } = textAreaRef.current;
      if (scrollHeight > 0) {
        setTextAreaHeight(scrollHeight);
      }
    }, [inputValue]);
    useImperativeHandle(forwardedRef, () => ({
      blur() {
        setEditMode(false);
      },
      focus() {
        if (!disabled) {
          setEditMode(true);
        }
      }
    }));
    useImperativeHandle(keyboardRef, () => textAreaRef.current);
    return /* @__PURE__ */ jsxs(InPlaceInputWrapper, { children: [
      /* @__PURE__ */ jsx(
        InPlaceInputTextArea,
        {
          ref: textAreaRef,
          disabled,
          height: textAreaHeight,
          onBlur: commitChanges,
          onChange: handleChange,
          onClick: disabled ? noop : enterEditMode,
          onKeyDown: handleKeyDown,
          placeholder,
          readOnly: !editMode,
          spellCheck: editMode,
          value: inputValue,
          variant,
          wrap: "soft"
        }
      ),
      !hideEditButtonOnDisabled && /* @__PURE__ */ jsx(InPlaceInputButtonWrapper, { $editMode: editMode, children: /* @__PURE__ */ jsx(
        UtilityButton,
        {
          disabled,
          icon: editMode ? "check" : "edit",
          magnitude: "small",
          onClick: editMode ? noop : enterEditMode
        },
        editMode ? "commit" : "edit"
      ) })
    ] });
  }
);
InPlaceInput.propTypes = propTypes;
InPlaceInput.defaultProps = defaultProps;
export {
  InPlaceInput
};
